import React from 'react';
import Hero from '../components/Hero';
import About from '../components/About';
import Services from '../components/Services';
import Projects from '../components/Projects';
import Location from '../components/Location';
import Gallery from '../components/Gallery';
import ContactForm from '../components/ContactForm';
import ContactInfo from '../components/ContactInfo';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { Leaf, TrendingUp, Globe, Users } from 'lucide-react';

// Stats Banner Component
const StatsBanner = () => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  const stats = [
    { icon: Leaf, value: '890,000', label: 'Ton CO₂ Secuestradas', color: 'var(--primary-400)' },
    { icon: TrendingUp, value: '125,000+', label: 'Créditos Verificados', color: 'var(--accent-400)' },
    { icon: Globe, value: '45,000', label: 'Hectáreas Conservadas', color: '#60a5fa' },
    { icon: Users, value: '32', label: 'Socios Estratégicos', color: '#a78bfa' },
  ];

  return (
    <section style={{
      background: 'var(--dark-900)',
      borderTop: '1px solid var(--border)',
      borderBottom: '1px solid var(--border)',
      padding: '60px 0',
    }}>
      <div className="container" ref={ref}>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: '40px',
        }} className="stats-banner">
          {stats.map((stat, i) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              style={{ textAlign: 'center' }}
            >
              <stat.icon size={28} style={{ color: stat.color, marginBottom: '12px' }} />
              <div style={{
                fontSize: '2rem',
                fontWeight: 800,
                background: 'var(--gradient-primary)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: '6px',
              }}>
                {stat.value}
              </div>
              <div style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
                {stat.label}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .stats-banner {
            grid-template-columns: repeat(2, 1fr) !important;
            gap: 30px !important;
          }
        }
      `}</style>
    </section>
  );
};

// Testimonials Section
const Testimonials = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const testimonials = [
    {
      quote: "CarbonTech transformó nuestra estrategia de sostenibilidad. En 18 meses logramos neutralidad de carbono en nuestras operaciones paraguayas.",
      author: "Carlos Mendoza",
      role: "Director de Sostenibilidad",
      company: "Grupo Industrial del Este",
    },
    {
      quote: "La tecnología de monitoreo satelital nos da confianza total. Sabemos exactamente cuánto carbono estamos capturando en tiempo real.",
      author: "Ana Ferreira",
      role: "CEO",
      company: "EcoBrasil Paraguay",
    },
    {
      quote: "Como pequeño productor, nunca pensé que podría acceder al mercado de carbono. CarbonTech hizo todo el proceso posible y rentable.",
      author: "Pedro González",
      role: "Productor Agrícola",
      company: "Cooperativa Naranjal",
    },
  ];

  return (
    <section style={{
      background: 'var(--dark)',
      padding: 'var(--space-3xl) 0',
    }}>
      <div className="container" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Testimonios
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Lo que Dicen Nuestros <span className="text-gradient">Socios</span>
          </h2>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: '24px',
        }} className="testimonials-grid">
          {testimonials.map((t, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: i * 0.15 }}
              style={{
                background: 'var(--dark-800)',
                borderRadius: '20px',
                padding: '32px',
                border: '1px solid var(--border)',
                position: 'relative',
              }}
            >
              <div style={{
                fontSize: '3rem',
                color: 'var(--primary-600)',
                lineHeight: 1,
                marginBottom: '16px',
                fontFamily: 'Georgia, serif',
              }}>
                "
              </div>
              <p style={{
                color: 'var(--text-secondary)',
                lineHeight: 1.7,
                fontSize: '0.95rem',
                marginBottom: '24px',
                fontStyle: 'italic',
              }}>
                {t.quote}
              </p>
              <div style={{
                borderTop: '1px solid var(--border)',
                paddingTop: '20px',
              }}>
                <div style={{ fontWeight: 600, marginBottom: '4px' }}>{t.author}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '0.85rem' }}>{t.role}</div>
                <div style={{ color: 'var(--primary-400)', fontSize: '0.85rem' }}>{t.company}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .testimonials-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const { ref, inView } = useInView({
    threshold: 0.3,
    triggerOnce: true,
  });

  return (
    <section style={{
      background: 'var(--gradient-dark)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <div style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        width: '800px',
        height: '800px',
        background: 'radial-gradient(circle, rgba(16,185,129,0.06) 0%, transparent 70%)',
        borderRadius: '50%',
        pointerEvents: 'none',
      }} />

      <div className="container" ref={ref} style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          style={{
            textAlign: 'center',
            maxWidth: '800px',
            margin: '0 auto',
          }}
        >
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3.5rem)',
            marginBottom: '20px',
          }}>
            ¿Listo para un Futuro{' '}
            <span className="text-gradient">Carbono Neutral</span>?
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.15rem',
            lineHeight: 1.7,
            marginBottom: '40px',
          }}>
            Únase a las empresas líderes de Paraguay y Brasil que ya están 
            transformando su impacto ambiental con CarbonTech.
          </p>
          <div style={{
            display: 'flex',
            gap: '16px',
            justifyContent: 'center',
            flexWrap: 'wrap',
          }}>
            <a
              href="#contacto"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contacto').scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                padding: '16px 40px',
                background: 'var(--gradient-primary)',
                color: 'white',
                borderRadius: '50px',
                fontWeight: 600,
                fontSize: '1rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                transition: 'all 0.3s ease',
                boxShadow: 'var(--shadow-glow)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-3px)';
                e.currentTarget.style.boxShadow = '0 0 60px rgba(16, 185, 129, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'var(--shadow-glow)';
              }}
            >
              <Leaf size={20} />
              Iniciar Proyecto
            </a>
            <a
              href="#servicios"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#servicios').scrollIntoView({ behavior: 'smooth' });
              }}
              style={{
                padding: '16px 40px',
                background: 'transparent',
                color: 'var(--text-primary)',
                borderRadius: '50px',
                fontWeight: 600,
                fontSize: '1rem',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '10px',
                border: '2px solid rgba(255,255,255,0.1)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = 'var(--primary-400)';
                e.currentTarget.style.color = 'var(--primary-400)';
                e.currentTarget.style.transform = 'translateY(-3px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.1)';
                e.currentTarget.style.color = 'var(--text-primary)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              Explorar Servicios
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Contact Section Wrapper
const ContactSection = () => {
  return (
    <section id="contacto" style={{
      background: 'var(--dark)',
      padding: 'var(--space-3xl) 0',
      position: 'relative',
    }}>
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '50%',
        background: 'radial-gradient(ellipse at bottom, rgba(16,185,129,0.03) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <div className="container" style={{ position: 'relative', zIndex: 1 }}>
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          style={{ textAlign: 'center', maxWidth: '700px', margin: '0 auto 60px' }}
        >
          <span style={{
            display: 'inline-block',
            padding: '8px 20px',
            background: 'rgba(16, 185, 129, 0.1)',
            border: '1px solid rgba(16, 185, 129, 0.2)',
            borderRadius: '50px',
            color: 'var(--primary-400)',
            fontSize: '0.85rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '2px',
            marginBottom: '20px',
          }}>
            Contacto
          </span>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'clamp(2rem, 4vw, 3rem)',
            marginBottom: '20px',
          }}>
            Conectemos <span className="text-gradient">Negocios</span>
          </h2>
          <p style={{
            color: 'var(--text-secondary)',
            fontSize: '1.1rem',
            lineHeight: 1.7,
          }}>
            Estamos listos para atender sus necesidades de carbono. Contáctenos para 
            cotizaciones de créditos, desarrollo de proyectos o alianzas estratégicas.
          </p>
        </motion.div>

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '50px',
        }} className="contact-grid">
          <ContactForm />
          <ContactInfo />
        </div>
      </div>

      <style>{`
        @media (max-width: 1024px) {
          .contact-grid {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </section>
  );
};

// Main Home Page
const Home = () => {
  return (
    <main>
      <Hero />
      <StatsBanner />
      <About />
      <Services />
      <Projects />
      <Location />
      <Gallery />
      <Testimonials />
      <CTASection />
      <ContactSection />
    </main>
  );
};

export default Home;
